import { ALL_PERMIT_TYPE_JURISDICTION_CODES } from '@contracts/order/valueNormalizers/PermitTypeJurisdiction';
import { TransactionCurrencyValues } from '@contracts/payment/Currency';
import { PermitType, permitTypeUnit } from '@contracts/permitType/PermitType';
import { Checkbox } from '@src/common/components/Checkbox';
import { MenuItem, Select } from '@src/common/components/Select';
import { Stack } from '@src/common/components/Stack';
import { TextInput } from '@src/common/components/TextInput';
import { theme } from '@src/common/styles/Theme';
import type { AddPermitTypeFormInputs } from '@src/permitTypesManager/AddPermitTypeFormInputs';
import type { SectionField } from '@src/permitTypesManager/permitTypeSections/PermitTypeSectionCheboxList';
import { PermitTypeSectionCheckboxList } from '@src/permitTypesManager/permitTypeSections/PermitTypeSectionCheboxList';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  CurrencyField,
  HighPriorityField,
  JurisdictionField,
  JurisdictionUrlField,
  NameField,
  ShowToClientField,
  TypeField,
  UnitSystemFields,
  ValidateExpirationDateField,
  ValidateExpirationTimeField,
} from './PermitDefinitionFields';

export const PermitDefinitionSection = () => {
  const { t } = useTranslation();
  const {
    register,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<AddPermitTypeFormInputs>();
  const currencies = TransactionCurrencyValues;

  const validateExpirationTimeValue = watch(
    ValidateExpirationTimeField.fieldName,
  );

  const fields: SectionField[] = [
    {
      field: HighPriorityField,
      label: t('permitTypesManager.addPermitType.definition.highPriority'),
    },
    {
      field: ShowToClientField,
      label: t('permitTypesManager.addPermitType.definition.showToClient'),
    },
  ];

  return (
    <Stack direction="column" gap={theme.spacing['1']}>
      <Controller
        name={JurisdictionField.fieldName}
        control={control}
        defaultValue=""
        render={({ field }) => (
          <Select
            fullWidth
            label={t(
              'permitTypesManager.addPermitType.definition.jurisdiction',
            )}
            {...field}
            error={errors.type && t(`formErrors.${errors.type.message}`)}
          >
            {ALL_PERMIT_TYPE_JURISDICTION_CODES.map((jurisdiction) => (
              <MenuItem key={jurisdiction} value={jurisdiction}>
                {jurisdiction}
              </MenuItem>
            ))}
          </Select>
        )}
      />
      <Controller
        name={TypeField.fieldName}
        control={control}
        render={({ field }) => (
          <Select
            fullWidth
            label={t('permitTypesManager.addPermitType.definition.permitType')}
            {...field}
            error={errors.type && t(`formErrors.${errors.type.message}`)}
          >
            {PermitType.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        )}
      />
      <Controller
        name={CurrencyField.fieldName}
        control={control}
        render={({ field }) => (
          <Select
            fullWidth
            label={t('permitTypesManager.addPermitType.definition.currency')}
            {...field}
            error={
              errors.currency && t(`formErrors.${errors.currency.message}`)
            }
          >
            {currencies.sort().map((currency) => (
              <MenuItem key={currency} value={currency}>
                {currency}
              </MenuItem>
            ))}
          </Select>
        )}
      />
      <Controller
        name={UnitSystemFields.fieldName}
        control={control}
        render={({ field }) => (
          <Select
            fullWidth
            label={t('permitTypesManager.addPermitType.definition.units.title')}
            {...field}
            error={errors.unit && t(`formErrors.${errors.unit.message}`)}
          >
            <MenuItem value={permitTypeUnit.IMPERIAL}>
              {t('permitTypesManager.addPermitType.definition.units.imperial')}
            </MenuItem>
            <MenuItem value={permitTypeUnit.METRIC}>
              {t('permitTypesManager.addPermitType.definition.units.metric')}
            </MenuItem>
          </Select>
        )}
      />
      <TextInput
        label={t('permitTypesManager.addPermitType.definition.permitName')}
        {...register(NameField.fieldName)}
        error={errors.name && t(`formErrors.${errors.name.message}`)}
      ></TextInput>
      <TextInput
        label={t('permitTypesManager.addPermitType.definition.jurisdictionURL')}
        {...register(JurisdictionUrlField.fieldName)}
        error={
          errors.jurisdictionUrl &&
          t(`formErrors.${errors.jurisdictionUrl.message}`)
        }
      ></TextInput>
      <PermitTypeSectionCheckboxList sectionFields={fields} control={control} />
      <Controller
        name={ValidateExpirationDateField.fieldName}
        control={control}
        render={({ field }) => (
          <Checkbox
            label={t(
              'permitTypesManager.addPermitType.definition.validateExpirationDate',
            )}
            onChange={(e) => {
              const checked = e.target.checked;
              field.onChange(checked);
              if (!checked && validateExpirationTimeValue) {
                setValue(ValidateExpirationTimeField.fieldName, false);
              }
            }}
            checked={!!field.value}
            sx={{ my: -1 }}
          />
        )}
      />
      <Controller
        name={ValidateExpirationTimeField.fieldName}
        control={control}
        render={({ field }) => (
          <Checkbox
            label={t(
              'permitTypesManager.addPermitType.definition.validateExpirationTime',
            )}
            onChange={(e) => {
              const checked = e.target.checked;
              field.onChange(checked);
              if (checked) {
                setValue(ValidateExpirationDateField.fieldName, true);
              }
            }}
            checked={!!field.value}
            sx={{ my: -1 }}
          />
        )}
      />
    </Stack>
  );
};
